import { Suspense, lazy, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, ArrowUpRight } from "lucide-react";
import { MaskText } from "@/components/motion";
import { SceneBoundary } from "@/components/three/SceneBoundary";
import { COLLECTIONS, PAN_TEXTURE, SHADES, SHADE_PRICE, formatINR, productUrl } from "@/data/catalog";
import { useLiveProduct } from "@/lib/live";
import { EASE, hasWebGL, scrollToId } from "@/lib/scroll";
import { useInViewActive } from "@/lib/useInViewActive";

const HERO_SHADES = SHADES.filter((s) => ["champagne-comet", "moonlit-dust", "molten-muse", "chromaflare", "blush-beam", "pink-nebula"].includes(s.id));
const PAN_URLS = HERO_SHADES.map((s) => PAN_TEXTURE(s.id));

const HeroScene = lazy(() => import("@/components/HeroScene"));

// Genuine glitter pan photo (Shopify container shoot) shown when WebGL is unavailable or still loading.
const StaticFallback = ({ pan }) => (
  <div className="absolute inset-0 grid place-items-center" data-testid="hero-3d-fallback">
    <img src={pan} alt="" className="h-[70%] w-[70%] rounded-full object-cover opacity-90 [mask-image:radial-gradient(circle,black_55%,transparent_72%)] sm:h-[72%] sm:w-[72%]" />
  </div>
);

const Frame = ({ src, className, delay, mx, my, depth }) => {
  const x = useTransform(mx, (v) => v * depth);
  const y = useTransform(my, (v) => v * depth);
  return (
    <motion.div style={{ x, y }} className={`pointer-events-none absolute z-20 overflow-hidden ${className}`}>
      <AnimatePresence initial={false}>
        <motion.img
          key={src}
          src={src}
          alt=""
          initial={{ clipPath: "inset(100% 0% 0% 0%)", scale: 1.25 }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)", scale: 1 }}
          exit={{ opacity: 1, transition: { duration: 1 } }}
          transition={{ duration: 1.2, ease: EASE, delay }}
          className="absolute inset-0 h-full w-full object-cover"
        />
      </AnimatePresence>
    </motion.div>
  );
};

const ShadePicker = ({ idx, setIdx, onEnter, onLeave }) => {
  const s = HERO_SHADES[idx];
  const live = useLiveProduct(s);
  const price = live.live ? live.price : SHADE_PRICE.price;
  return (
    <motion.div
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, ease: EASE, delay: 1.4 }}
      data-testid="hero-shade-picker"
      className="absolute bottom-3 left-5 right-5 z-30 rounded-2xl border border-champagne/25 bg-ink/55 p-3 backdrop-blur-xl sm:left-8 sm:right-auto sm:w-[340px] lg:bottom-6 lg:left-2 lg:cursor-auto"
    >
      <div className="flex items-center justify-between gap-3">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-champagne">Celestial eyeshadow</p>
        <a href={productUrl(s.handle)} data-testid="hero-shade-shop-link" className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-cream hover:text-champagne">
          {live.available === false ? "Sold out" : `Shop ${formatINR(price)}`} <ArrowUpRight className="h-3.5 w-3.5" />
        </a>
      </div>
      <p data-testid="hero-shade-name" className="mt-1 font-display text-xl text-cream sm:text-2xl">{s.name}</p>
      <div className="mt-2 flex gap-2" role="radiogroup" aria-label="Hero shade">
        {HERO_SHADES.map((sh, i) => (
          <button
            key={sh.id}
            role="radio"
            aria-checked={i === idx}
            aria-label={sh.name}
            data-testid={`hero-shade-${sh.id}`}
            onClick={() => setIdx(i)}
            className={`swatch-dot h-8 w-8 rounded-full transition-transform duration-500 hover:scale-110 ${i === idx ? "ring-2 ring-champagne ring-offset-2 ring-offset-ink" : ""}`}
            style={{ background: `radial-gradient(circle at 32% 28%, #fff8ec 0%, ${sh.colors[0]} 24%, ${sh.colors[1]} 78%)` }}
          />
        ))}
      </div>
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const stage = useRef(null);
  const [open, setOpen] = useState(true);
  const [shadeIdx, setShadeIdx] = useState(0);
  const [hovering, setHovering] = useState(false);
  const shade = HERO_SHADES[shadeIdx];
  const pan = PAN_TEXTURE(shade.id);
  const webgl = useMemo(hasWebGL, []);
  const active = useInViewActive(stage);
  const isPhone = typeof window !== "undefined" && window.innerWidth < 640;

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const sceneY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0.15]);

  const mx = useSpring(useMotionValue(0), { stiffness: 80, damping: 20 });
  const my = useSpring(useMotionValue(0), { stiffness: 80, damping: 20 });
  const cx = useMotionValue(-100);
  const cy = useMotionValue(-100);

  const onMove = (e) => {
    const r = stage.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
    cx.set(e.clientX - r.left);
    cy.set(e.clientY - r.top);
  };

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden">
      <div aria-hidden className="hero-glow" />
      <div className="relative mx-auto grid min-h-[100svh] max-w-[1440px] grid-cols-1 px-5 pb-16 pt-[calc(7rem_+_var(--sale-strip,0px))] sm:px-8 sm:pt-[calc(8rem_+_var(--sale-strip,0px))] lg:grid-cols-12 lg:px-12 lg:pt-[calc(9rem_+_var(--sale-strip,0px))]">
        <motion.div style={{ y: textY, opacity: fade }} className="relative z-30 flex flex-col justify-center lg:col-span-6">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
            className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-champagne"
          >
            WINK by Ace Vanity — The Beauty Edit
          </motion.p>
          <h1 data-testid="hero-title" className="font-display text-[3.2rem] font-light leading-[0.9] tracking-[-0.02em] text-cream sm:text-7xl lg:text-[6.4rem] xl:text-[7.4rem]">
            <MaskText
              onLoad
              delay={0.35}
              lines={[
                "Pressed",
                <>
                  <em className="shimmer-text font-normal italic">light,</em> worn
                </>,
                "like a secret.",
              ]}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 0.9 }}
            className="mt-8 max-w-md text-sm leading-relaxed text-stone sm:text-base"
          >
            Glitter palettes, pressed and loose pigments, holo liners, lashes, blenders and brushes. Eleven collections, one effortless edit for eyes that catch the light.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-3"
          >
            <button data-testid="hero-explore-button" onClick={() => scrollToId("collections")} className="btn-gold group">
              Explore the {COLLECTIONS.length} collections
              <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
            </button>
            <button data-testid="hero-bestsellers-button" onClick={() => scrollToId("bestsellers")} className="btn-ghost">
              Shop best sellers
            </button>
          </motion.div>
          <motion.dl
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.3 }}
            className="mt-12 grid max-w-md grid-cols-3 gap-4 border-t border-white/10 pt-6"
          >
            {[["Est.", "2021"], ["Collections", String(COLLECTIONS.length).padStart(2, "0")], ["Lashes from", "₹199"]].map(([k, v]) => (
              <div key={k}>
                <dt className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone">{k}</dt>
                <dd className="mt-1 font-display text-2xl text-cream">{v}</dd>
              </div>
            ))}
          </motion.dl>
        </motion.div>

        <motion.div
          ref={stage}
          style={{ y: sceneY }}
          onMouseMove={onMove}
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          data-testid="hero-3d-stage"
          className="relative -mx-5 mt-6 h-[58svh] min-h-[460px] sm:-mx-8 sm:h-[60svh] lg:col-span-6 lg:mx-0 lg:mt-0 lg:h-[calc(100svh-170px)] lg:min-h-[560px] lg:self-center lg:cursor-none"
        >
          <div aria-hidden className="absolute inset-[8%] rounded-full bg-[radial-gradient(circle,rgba(186,122,45,0.28),transparent_65%)] blur-2xl" />
          {webgl ? (
            <SceneBoundary fallback={<StaticFallback pan={pan} />}>
              <Suspense fallback={<StaticFallback pan={pan} />}>
                <HeroScene open={open} onToggle={() => setOpen((o) => !o)} colors={shade.colors} pan={pan} pans={PAN_URLS} active={active} compact={isPhone} />
              </Suspense>
            </SceneBoundary>
          ) : (
            <StaticFallback pan={pan} />
          )}
          <Frame src={shade.look} className="right-[4%] top-[2%] h-28 w-20 sm:h-44 sm:w-32 lg:right-[2%] lg:top-[6%] lg:h-56 lg:w-40" delay={0} mx={mx} my={my} depth={-26} />
          <ShadePicker idx={shadeIdx} setIdx={setShadeIdx} onEnter={() => setHovering(false)} onLeave={() => setHovering(true)} />
          <button
            data-testid="hero-eyeshadow-toggle"
            onClick={() => setOpen((o) => !o)}
            onMouseEnter={() => setHovering(false)}
            onMouseLeave={() => setHovering(true)}
            className="absolute left-5 top-2 z-30 rounded-full border border-champagne/30 bg-ink/60 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.22em] text-champagne backdrop-blur-md transition-colors hover:bg-champagne hover:text-ink sm:left-8 lg:bottom-6 lg:left-auto lg:right-6 lg:top-auto lg:cursor-pointer"
          >
            {open ? "Close the eyeshadow" : "Open the eyeshadow"}
          </button>
          <motion.div
            aria-hidden
            style={{ x: cx, y: cy }}
            animate={{ scale: hovering ? 1 : 0, opacity: hovering ? 1 : 0 }}
            transition={{ duration: 0.35 }}
            className="pointer-events-none absolute left-0 top-0 z-40 hidden lg:block"
          >
            <div className="-translate-x-1/2 -translate-y-1/2 grid h-24 w-24 place-items-center rounded-full border border-champagne/60 bg-champagne/10 text-center font-mono text-[10px] uppercase leading-tight tracking-[0.2em] text-champagne backdrop-blur-sm">
              {open ? "Tap to\nclose" : "Tap to\nopen"}
            </div>
          </motion.div>
        </motion.div>
      </div>
      <button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToId("collections")}
        className="absolute bottom-6 left-1/2 z-30 hidden -translate-x-1/2 items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-stone transition-colors hover:text-champagne lg:flex"
      >
        Scroll <ArrowDown className="h-3.5 w-3.5 animate-bounce" />
      </button>
    </section>
  );
};
